import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import Reveal, { MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { CTASection } from "@/pages/Home";
import { services } from "@/data/content";

export default function ServicePage() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);

  useEffect(() => {
    if (service) {
      document.title = `${service.title} in Charleston, SC | Rogers American Construction`;
    }
  }, [service]);

  if (!service) return <Navigate to="/" replace />;

  return (
    <main>
      <section className="relative min-h-[85vh] flex flex-col justify-end overflow-hidden bg-ink text-paper" data-testid="service-hero">
        <div className="absolute inset-0">
          <img src={service.img} alt={service.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/65" />
        </div>
        <div className="relative z-10 px-6 md:px-12 pt-40 pb-16">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="text-xs md:text-sm uppercase tracking-[0.25em] text-paper/70 mb-6"
          >
            {service.num} — {service.tag}
          </motion.p>
          <h1 className="font-display font-medium uppercase leading-[0.92] tracking-tighter text-[11vw] md:text-[7vw]" data-testid="service-title">
            <MaskedLine delay={0.1}>{service.title}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 max-w-2xl text-paper/75 text-base md:text-lg leading-relaxed"
            data-testid="service-intro"
          >
            {service.intro}
          </motion.p>
        </div>
      </section>

      <Marquee items={["No project too large", "No project too small", "Licensed & Insured", "South Carolina"]} />

      <section className="px-6 md:px-12 py-20 md:py-28" data-testid="service-scope">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-4">Scope</p>
            <h2 className="font-display font-medium uppercase tracking-tighter text-3xl md:text-4xl leading-[0.95]">
              What is included
            </h2>
          </Reveal>
          <div className="md:col-span-8 border-t border-l border-ink/10 grid grid-cols-1 sm:grid-cols-2">
            {service.points.map((p, i) => (
              <Reveal key={p} delay={i * 0.05}>
                <div className="flex items-center gap-4 border-b border-r border-ink/10 p-6" data-testid={`service-point-${i}`}>
                  <Check className="w-5 h-5 text-accent shrink-0" strokeWidth={2.5} />
                  <span className="font-semibold text-base">{p}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10 px-6 md:px-12 py-20 md:py-24" data-testid="service-promise">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <Reveal className="md:col-span-8">
            <p className="font-display font-medium text-3xl md:text-5xl leading-[1.05] tracking-tight">
              Every {service.title.toLowerCase()} job starts with a site walk and a
              clear estimate — and ends with a walkthrough, together.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-4 md:text-right">
            <Link
              to="/request-quote"
              data-testid="service-quote-button"
              className="group inline-flex items-center gap-3 bg-ink text-paper font-semibold uppercase tracking-widest text-sm px-8 py-4 hover:bg-accent transition-colors"
            >
              Price my project
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
